import type { SimplePermissionSchema, UpdateOrderParamsDTO } from '@app/models/orders';

/**
 * Per-field permissions for a field group as returned by the backend in
 * `RetrieveOrderSchema.$field_groups`. The generated model only describes the
 * group-level `{ view, update }`, but the retrieve response also carries
 * per-field permissions under `fields`, which we need for PATCH-style filtering.
 */
export interface FieldGroupPermissionSchema extends SimplePermissionSchema {
  fields?: Record<string, SimplePermissionSchema>;
}

/**
 * Trims an update-order payload down to what the current user is actually
 * allowed to write, based on the per-field permissions from the retrieve
 * response. This turns "send the whole form" into PATCH semantics, so the
 * backend never sees a key the UI could not have changed (e.g. it stops the
 * 422 `warehouse_update_restricted_field` on warehouse stages).
 *
 * Rules:
 * - keep `field_groups[group][field]` only when
 *   `fieldGroupsPermissions[group].fields[field].update === true`;
 * - drop groups left empty after filtering;
 * - keep `artifacts[uid]` only when `artifactsPermissions[uid].update === true`;
 * - emptied-out `field_groups`/`artifacts` are sent as `{}` (never `null`: the
 *   backend DTOs are non-optional dicts and reject `null`);
 * - pass `version` through untouched.
 *
 * Returns a new object — the caller's form state is never mutated.
 */
export const filterPayloadByPermissions = (
  payload: UpdateOrderParamsDTO,
  fieldGroupsPermissions: Record<string, FieldGroupPermissionSchema> | null | undefined,
  artifactsPermissions: Record<string, SimplePermissionSchema> | null | undefined
): UpdateOrderParamsDTO => {
  const result: UpdateOrderParamsDTO = { version: payload.version };

  if (payload.field_groups) {
    const filteredGroups: Record<string, Record<string, unknown>> = {};

    Object.entries(payload.field_groups).forEach(([groupKey, groupValue]) => {
      const fieldsPermissions = fieldGroupsPermissions?.[groupKey]?.fields;
      if (!fieldsPermissions || !groupValue) {
        return;
      }

      const filteredFields: Record<string, unknown> = {};
      Object.entries(groupValue as Record<string, unknown>).forEach(([fieldKey, fieldValue]) => {
        if (fieldsPermissions[fieldKey]?.update === true) {
          filteredFields[fieldKey] = fieldValue;
        }
      });

      if (Object.keys(filteredFields).length > 0) {
        filteredGroups[groupKey] = filteredFields;
      }
    });

    result.field_groups = filteredGroups;
  }

  if (payload.artifacts) {
    const filteredArtifacts: Record<string, object> = {};

    Object.entries(payload.artifacts).forEach(([artifactUid, artifactValue]) => {
      if (artifactsPermissions?.[artifactUid]?.update === true) {
        filteredArtifacts[artifactUid] = artifactValue;
      }
    });

    result.artifacts = filteredArtifacts;
  }

  return result;
};

/**
 * Returns true when at least one field or artifact is writable, i.e. the user
 * can actually change something in the edit form. Used to disable the Save
 * button when nothing is editable (a defensive corner case: normally the
 * backend would already report `$operations.update === false` in that case).
 */
export const hasWritablePermissions = (
  fieldGroupsPermissions: Record<string, FieldGroupPermissionSchema> | null | undefined,
  artifactsPermissions: Record<string, SimplePermissionSchema> | null | undefined
): boolean => {
  const hasWritableField = Object.values(fieldGroupsPermissions ?? {}).some((group) =>
    Object.values(group?.fields ?? {}).some((field) => field?.update === true)
  );
  if (hasWritableField) {
    return true;
  }
  return Object.values(artifactsPermissions ?? {}).some((artifact) => artifact?.update === true);
};
